import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { Document, Page } from "react-pdf";

import { handleMouseDown } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseDown";
import { handleMouseMove } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseMove";
import { handleMouseUp } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseUp";

import InteractionRectOverlay from "./overlays/InteractionRectOverlay";
import { createCanvasPointerScheduler } from "./canvasPointerScheduler";
import { createWidgetMovePreview } from "./widgetMovePreview";
import FieldToolPreviewOverlay from "./overlays/FieldToolPreviewOverlay";
import FormFieldOverlayLayer from "./overlays/FormFieldOverlayLayer";
import PdfTextOverlayLayer from "./overlays/PdfTextOverlayLayer";
import AnnotationFreePdfCanvas from "./AnnotationFreePdfCanvas";
import {
  canvasToPdfPoints,
  canvasDeltaToPdfDelta,
} from "../../../../../../utils/coordinateUtils";
import { getEffectivePageRotation } from "../utils/documentOrientation";
import {
  copySelectedFieldGeometry,
  duplicateCopiedFields,
} from "../utils/fieldClipboard";
import { flushSync } from "react-dom";

const createExtraFieldName = () => {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return `extra_field_${crypto.randomUUID()}`;
  }

  return `extra_field_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const isEditableTarget = (target) => {
  if (!target || typeof target.closest !== "function") {
    return false;
  }

  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="textbox"]',
    ),
  );
};

const hasNativeTextSelection = () => {
  if (typeof window === "undefined" || !window.getSelection) return false;
  const selection = window.getSelection();
  return Boolean(selection && selection.toString().length > 0);
};

const getSelectedTextWithinContainer = (container) => {
  if (!container || typeof window === "undefined" || !window.getSelection) {
    return "";
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return "";
  }

  const range = selection.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) {
    return "";
  }

  return selection.toString().replace(/\s+/g, " ").trim();
};

const PDF_ZOOM_RENDER_DELAY = 220;

const TEXT_ITEM_RECT_TOLERANCE = 0.5;

const hasSameTextItems = (previousItems, nextItems) => {
  if (
    !Array.isArray(previousItems) ||
    previousItems.length !== nextItems.length
  ) {
    return false;
  }

  return previousItems.every((previous, index) => {
    const next = nextItems[index];

    return (
      previous?.id === next.id &&
      previous?.text === next.text &&
      Math.abs(previous.rect.X - next.rect.X) < TEXT_ITEM_RECT_TOLERANCE &&
      Math.abs(previous.rect.Y - next.rect.Y) < TEXT_ITEM_RECT_TOLERANCE &&
      Math.abs(previous.rect.W - next.rect.W) < TEXT_ITEM_RECT_TOLERANCE &&
      Math.abs(previous.rect.H - next.rect.H) < TEXT_ITEM_RECT_TOLERANCE
    );
  });
};

// Keep React-PDF's layers untouched while only the displayed zoom is changing.
const PdfPageLayers = React.memo(
  ({
    pageNumber,
    scale,
    displayRotation,
    canvasRef,
    onLoad,
    onRender,
    onText,
  }) => {
    const CustomCanvas = useCallback(
      () => (
        <AnnotationFreePdfCanvas
          canvasRef={canvasRef}
          onRenderSuccess={onRender}
        />
      ),
      [canvasRef, onRender],
    );

    return (
      <Page
        pageNumber={pageNumber}
        scale={scale}
        rotate={displayRotation}
        renderMode="custom"
        customRenderer={CustomCanvas}
        renderTextLayer
        renderAnnotationLayer={false}
        className="custom-react-pdf"
        onLoadSuccess={onLoad}
        onRenderTextLayerSuccess={onText}
        onLoadError={(error) => console.error("Page load error:", error)}
      />
    );
  },
);

PdfPageLayers.displayName = "PdfPageLayers";

const ZoomablePdfPage = ({
  pageNumber,
  scale,
  displayRotation,
  onPageLoadSuccessRef,
  buildPdfTextItemsForPageRef,
}) => {
  const [renderScale, setRenderScale] = useState(scale);
  const [pageSize, setPageSize] = useState(null);
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const canvasRef = useRef(null);
  const snapshotRef = useRef(null);
  const activeRenderScaleRef = useRef(renderScale);
  const mountedRef = useRef(false);
  const hasNotifiedLoadRef = useRef(false);

  useLayoutEffect(() => {
    mountedRef.current = true;
    activeRenderScaleRef.current = renderScale;
    return () => {
      mountedRef.current = false;
    };
  }, [renderScale]);

  useEffect(() => {
    if (scale === renderScale) return;
    const timeout = window.setTimeout(
      () => setRenderScale(scale),
      PDF_ZOOM_RENDER_DELAY,
    );
    return () => window.clearTimeout(timeout);
  }, [scale, renderScale]);

  useEffect(() => {
    const snapshot = snapshotRef.current;
    return () => {
      if (snapshot) {
        snapshot.width = 0;
        snapshot.height = 0;
      }
    };
  }, []);

  const handleLoad = useCallback(
    (page) => {
      if (!mountedRef.current || activeRenderScaleRef.current !== renderScale) {
        return;
      }
      const viewport = page.getViewport({
        scale: 1,
        rotation: getEffectivePageRotation(page.rotate, displayRotation),
      });
      setPageSize((previous) =>
        previous?.width === viewport.width &&
        previous?.height === viewport.height
          ? previous
          : { width: viewport.width, height: viewport.height },
      );
      // Zoom already updates viewport/scaleFactor in applyScaleChange.
      // Page/rotation changes remount this component, so the first load still reports.
      if (hasNotifiedLoadRef.current) return;
      hasNotifiedLoadRef.current = true;
      onPageLoadSuccessRef.current?.(page);
    },
    [displayRotation, renderScale, onPageLoadSuccessRef],
  );

  const handleRender = useCallback(() => {
    if (!mountedRef.current || activeRenderScaleRef.current !== renderScale) {
      return;
    }
    const canvas = canvasRef.current;
    const snapshot = snapshotRef.current;
    if (!canvas?.width || !canvas?.height || !snapshot) return;
    const context = snapshot.getContext("2d", { alpha: false });
    if (!context) return;

    // Copy only a completed render. This canvas is never cleared by React-PDF
    // when its live canvas is replaced for the next resolution.
    snapshot.width = canvas.width;
    snapshot.height = canvas.height;
    context.drawImage(canvas, 0, 0);
    setHasSnapshot(true);
  }, [renderScale]);

  const handleText = useCallback(() => {
    if (!mountedRef.current || activeRenderScaleRef.current !== renderScale) {
      return;
    }
    buildPdfTextItemsForPageRef.current?.();
  }, [renderScale, buildPdfTextItemsForPageRef]);

  return (
    <Box
      style={{
        position: "relative",
        width: pageSize ? pageSize.width * scale : undefined,
        height: pageSize ? pageSize.height * scale : undefined,
        // A scaled-down layer must not leave its old size in the scroll area.
        overflow: scale !== renderScale ? "hidden" : undefined,
      }}
    >
      <canvas
        ref={snapshotRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: hasSnapshot ? "block" : "none",
          pointerEvents: "none",
        }}
      />
      <Box
        style={{
          position: "relative",
          width: pageSize ? pageSize.width * renderScale : undefined,
          height: pageSize ? pageSize.height * renderScale : undefined,
          transform: `scale(${scale / renderScale})`,
          transformOrigin: "top left",
        }}
        sx={
          hasSnapshot
            ? {
                "& .react-pdf__Page": {
                  backgroundColor: "transparent !important",
                },
                "& .react-pdf__Page__canvas": { opacity: 0 },
              }
            : undefined
        }
      >
        <PdfPageLayers
          pageNumber={pageNumber}
          scale={renderScale}
          displayRotation={displayRotation}
          canvasRef={canvasRef}
          onLoad={handleLoad}
          onRender={handleRender}
          onText={handleText}
        />
      </Box>
    </Box>
  );
};

const StablePdfPage = React.memo(
  ({
    file,
    pageNumber,
    scale,
    displayRotation,
    pdfDocumentRef,
    onDocumentLoadSuccessRef,
    onPageLoadSuccessRef,
    buildPdfTextItemsForPageRef,
  }) => {
    const [source, setSource] = useState({ file, version: 0 });
    if (source.file !== file) {
      // Reset the page snapshot before a different document can be painted.
      setSource({ file, version: source.version + 1 });
    }

    return (
      <Document
        key={source.version}
        file={file}
        onLoadSuccess={(pdf) => onDocumentLoadSuccessRef.current?.(pdf)}
        onLoadError={(e) => console.error("Document load error:", e)}
        ref={pdfDocumentRef}
      >
        <ZoomablePdfPage
          key={`${pageNumber}_${displayRotation}`}
          pageNumber={pageNumber}
          scale={scale}
          displayRotation={displayRotation}
          onPageLoadSuccessRef={onPageLoadSuccessRef}
          buildPdfTextItemsForPageRef={buildPdfTextItemsForPageRef}
        />
      </Document>
    );
  },
  (prev, next) =>
    prev.file === next.file &&
    prev.pageNumber === next.pageNumber &&
    prev.scale === next.scale &&
    prev.displayRotation === next.displayRotation,
);

StablePdfPage.displayName = "StablePdfPage";

const PDFCanvas = ({
  base64File,
  pdfInput,
  pdfScrollContainerRef,
  pdfContainerRef,
  pdfDocumentRef,
  currentProcessingFile,
  setCurrentProcessingFile,
  pageNumber,
  scale,
  displayRotation,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  setFocusedField,
  currCursor,
  unassignedFieldNames,
  fixedInputs,
  inputRef,
  childInputRef,
  resizing,
  setResizing,
  moving,
  setMoving,
  isDrawing,
  setIsDrawing,
  isSelecting,
  setIsSelecting,
  rect,
  setRect,
  selectionRect,
  setSelectionRect,
  freeSelectStartPoint,
  setFreeSelectStartPoint,
  startPoint,
  setStartPoint,
  initialMousePosition,
  setInitialMousePosition,
  initialInputSize,
  setInitialInputSize,
  inputPosition,
  setInputPosition,
  resizeDirection,
  setResizeDirection,
  setCurrCursor,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  handleFieldChange,
  undoRedoStats,
  activeEditorTool,
  handleEditorToolSelection,
  textSelectionMode,
  formFields,
  onNavigateToTaggedField,
  onSearchSelectedText,
  onFavouriteDrop,
  favouriteDropDisabled = false,
}) => {
  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor ||
    1;
  const pageProxyRef = useRef(null);
  const extractedTextLayerKeyRef = useRef(null);
  const copiedFieldGeometriesRef = useRef([]);
  const displayViewport =
    currentProcessingFile?.document?.tempFields?.currentDocument?.viewport;

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ""; // Chrome requires returnValue to be set

      return ""; // Some browsers require a return value
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const guardPushed = useRef(false);

  useEffect(() => {
    if (!guardPushed.current) {
      window.history.pushState(null, "", window.location.href);
      guardPushed.current = true;
    }

    const handlePopState = () => {
      const leave = window.confirm("Are you sure you want to leave this page?");
      if (leave) {
        window.removeEventListener("popstate", handlePopState);
        window.history.back();
      } else {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const onPageLoadSuccessRef = useRef((page) => {
    pageProxyRef.current = page;
    extractedTextLayerKeyRef.current = null;
    onPageLoadSuccess(page);
  });

  useLayoutEffect(() => {
    onPageLoadSuccessRef.current = (page) => {
      pageProxyRef.current = page;
      extractedTextLayerKeyRef.current = null;
      onPageLoadSuccess(page);
    };
  });

  useEffect(() => {
    if (!pageProxyRef.current) return;

    extractedTextLayerKeyRef.current = null;
    onPageLoadSuccessRef.current?.(pageProxyRef.current);
  }, [displayRotation]);

  const onDocumentLoadSuccessRef = useRef(onDocumentLoadSuccess);
  useLayoutEffect(() => {
    onDocumentLoadSuccessRef.current = onDocumentLoadSuccess;
  });

  const buildPdfTextItemsForPage = useCallback(() => {
    const textLayer = pdfContainerRef?.current?.querySelector(
      ".react-pdf__Page__textContent",
    );
    const pageNode =
      pdfContainerRef?.current?.querySelector(".react-pdf__Page");
    const page = pageProxyRef.current;
    if (!textLayer || !pageNode || !page?.getViewport) return;

    const viewport = page.getViewport({
      scale,
      rotation: getEffectivePageRotation(page.rotate, displayRotation),
    });
    const extractionKey = `${pageNumber}_${scale}_${Math.round(viewport.width)}_${Math.round(viewport.height)}`;

    if (extractedTextLayerKeyRef.current === extractionKey) {
      return;
    }

    const pageRect = pageNode.getBoundingClientRect();
    const spans = Array.from(textLayer.querySelectorAll("span"));
    const items = [];

    spans.forEach((span, spanIndex) => {
      const text = (span.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return;

      const spanRect = span.getBoundingClientRect();
      const relativeLeft = spanRect.left - pageRect.left;
      const relativeTop = spanRect.top - pageRect.top;
      const relativeWidth = spanRect.width;
      const relativeHeight = spanRect.height;

      const runRect = canvasToPdfPoints(
        relativeLeft,
        relativeTop,
        relativeWidth,
        relativeHeight,
        viewport,
      );

      items.push({
        id: `run_${pageNumber}_${spanIndex}`,
        page: pageNumber,
        kind: "run",
        text,
        rect: {
          X: runRect.x,
          Y: runRect.y,
          W: runRect.width,
          H: runRect.height,
        },
      });

      const words = text.split(/\s+/).filter(Boolean);
      const totalCharacters = words.join("").length || text.length || 1;
      let offsetChars = 0;

      words.forEach((word, wordIndex) => {
        const wordChars = word.length || 1;
        const wordWidthPx = (relativeWidth * wordChars) / totalCharacters;
        const wordLeftPx =
          relativeLeft + (relativeWidth * offsetChars) / totalCharacters;
        offsetChars += wordChars;

        const wordRect = canvasToPdfPoints(
          wordLeftPx,
          relativeTop,
          wordWidthPx,
          relativeHeight,
          viewport,
        );

        items.push({
          id: `word_${pageNumber}_${spanIndex}_${wordIndex}`,
          page: pageNumber,
          kind: "word",
          text: word,
          rect: {
            X: wordRect.x,
            Y: wordRect.y,
            W: wordRect.width,
            H: wordRect.height,
          },
        });
      });
    });

    setCurrentProcessingFile((prev) => {
      const previousItems =
        prev?.document?.tempFields?.pdfTextItemsByPage?.[pageNumber];

      // Same text at a new zoom: keep the old array so nothing re-renders.
      if (hasSameTextItems(previousItems, items)) return prev;

      return {
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            pdfTextItemsByPage: {
              ...(prev.document.tempFields.pdfTextItemsByPage || {}),
              [pageNumber]: items,
            },
          },
        },
      };
    });

    extractedTextLayerKeyRef.current = extractionKey;
  }, [
    displayRotation,
    pageNumber,
    pdfContainerRef,
    scale,
    setCurrentProcessingFile,
  ]);

  const buildPdfTextItemsForPageRef = useRef(buildPdfTextItemsForPage);
  useLayoutEffect(() => {
    buildPdfTextItemsForPageRef.current = buildPdfTextItemsForPage;
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      const isTypingInEditable = isEditableTarget(e.target);
      const isEscToEditTool = [
        "checkbox",
        "textField",
        "insertText",
        "rectangle",
        "line",
      ].includes(activeEditorTool);
      const tempFields = currentProcessingFile?.document?.tempFields;
      const selectedFields = copySelectedFieldGeometry(tempFields, pageNumber);
      const key = e.key?.toLowerCase();
      const isWithinPdfCanvas = pdfContainerRef.current?.contains(e.target);

      if (
        e.shiftKey &&
        !ctrl &&
        key === "f" &&
        !isTypingInEditable &&
        textSelectionMode &&
        selectToolActive
      ) {
        const selectedPdfText = [
          tempFields?.selectedPdfTextItem,
          ...(tempFields?.selectedExtraPdfTextItems || []),
        ]
          .map((item) => item?.text || "")
          .filter(Boolean)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
        const selectedText =
          selectedPdfText ||
          getSelectedTextWithinContainer(pdfContainerRef.current);

        if (selectedText) {
          e.preventDefault();
          onSearchSelectedText?.(selectedText);
          return;
        }
      }

      if (
        ctrl &&
        key === "c" &&
        activeEditorTool === "edit" &&
        selectedFields.length > 0 &&
        !hasNativeTextSelection() &&
        isWithinPdfCanvas
      ) {
        e.preventDefault();
        copiedFieldGeometriesRef.current = selectedFields;

        return;
      }

      if (
        ctrl &&
        key === "v" &&
        activeEditorTool === "edit" &&
        copiedFieldGeometriesRef.current.length > 0 &&
        !hasNativeTextSelection() &&
        isWithinPdfCanvas
      ) {
        e.preventDefault();

        setCurrentProcessingFile((prevFile) => {
          const prevTempFields = prevFile?.document?.tempFields || {};
          const newFields = duplicateCopiedFields({
            copiedFields: copiedFieldGeometriesRef.current,
            existingFields: prevTempFields.formFields || [],
            fallbackPage: pageNumber,
            createName: createExtraFieldName,
          });

          if (newFields.length === 0) return prevFile;

          return {
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevTempFields,
                formFields: [
                  ...(prevTempFields.formFields || []),
                  ...newFields,
                ],
                selectedFormField: newFields[0],
                selectedExtraFields: newFields,
                selectedPdfTextItem: null,
                selectedExtraPdfTextItems: [],
              },
            },
          };
        });

        return;
      }

      if (e.key === "Escape" && isEscToEditTool && !isTypingInEditable) {
        e.preventDefault();
        handleEditorToolSelection?.("edit");
      } else if (isTypingInEditable) {
        return;
      } else if (ctrl && key === "z" && !e.shiftKey) {
        e.preventDefault();
        undoRedoStats?.undo();
      } else if (ctrl && (key === "y" || (key === "z" && e.shiftKey))) {
        e.preventDefault();
        undoRedoStats?.redo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeEditorTool,
    currentProcessingFile,
    handleEditorToolSelection,
    onSearchSelectedText,
    pageNumber,
    selectToolActive,
    setCurrentProcessingFile,
    textSelectionMode,
    undoRedoStats,
    pdfContainerRef,
  ]);

  const interactionRectRef = useRef(null);
  const latestDrawingRectRef = useRef(rect);
  useLayoutEffect(() => {
    latestDrawingRectRef.current = rect;
  }, [rect, isDrawing]);

  const setDrawingRectForFrame = useCallback((nextRect) => {
    latestDrawingRectRef.current = nextRect;
    interactionRectRef.current?.setDrawingRect(nextRect);
  }, []);
  const setSelectionRectForFrame = useCallback((nextRect) => {
    interactionRectRef.current?.setSelectingRect(nextRect);
  }, []);

  const widgetMovePreviewRef = useRef(null);
  const widgetMoveControllerRef = useRef(null);
  if (!widgetMoveControllerRef.current) {
    widgetMoveControllerRef.current = createWidgetMovePreview();
  }

  const pointerHandlersRef = useRef(null);
  useLayoutEffect(() => {
    pointerHandlersRef.current = {
      active: resizing || moving || isDrawing || isSelecting,
      move: (point) => {
        // Move/resize frames stay local; shared geometry is committed on release.
        const preview = widgetMovePreviewRef.current;
        const isBoxSelection =
          isSelecting && !moving && !resizing && !isDrawing;

        const useLocalMove = (moving || resizing || isBoxSelection) && preview;

        const controller = widgetMoveControllerRef.current;

        const gestureKey = isBoxSelection
          ? freeSelectStartPoint
          : initialMousePosition;

        const file = useLocalMove
          ? controller.read(
              currentProcessingFile,
              gestureKey,
              isBoxSelection ? "selection" : "geometry",
            )
          : currentProcessingFile;

        const updateFile = useLocalMove
          ? (updater) => preview.setFile(controller.update(updater))
          : setCurrentProcessingFile;
        handleMouseMove(
          point,
          file,
          updateFile,
          pdfContainerRef,
          resizing,
          initialMousePosition,
          initialInputSize,
          inputPosition,
          resizeDirection,
          moving,
          isDrawing,
          startPoint,
          setDrawingRectForFrame,
          isSelecting,
          freeSelectStartPoint,
          setSelectionRectForFrame,
          pageNumber,
        );
      },
      finish: () => {
        let pendingFile = currentProcessingFile;
        let historyRecorded = false;

        const isEditingGesture = moving || resizing || isDrawing;

        const commitWithHistory = (update) => {
          const previousFile = pendingFile;

          // Check the result before submitting this update to React.
          pendingFile =
            typeof update === "function" ? update(previousFile) : update;

          const fieldsChanged =
            pendingFile?.document?.tempFields?.formFields !==
            previousFile?.document?.tempFields?.formFields;
          // Inserted text lives outside formFields; its creation and movement
          // need the same single pre-gesture undo snapshot as widget gestures.
          const textEditsChanged =
            pendingFile?.document?.tempFields?.pdfTextEdits !==
            previousFile?.document?.tempFields?.pdfTextEdits;

          if (
            isEditingGesture &&
            (fieldsChanged || textEditsChanged) &&
            !historyRecorded
          ) {
            undoRedoStats?.pushHistory?.();
            historyRecorded = true;
          }

          setCurrentProcessingFile(update);
        };

        widgetMoveControllerRef.current.commit(commitWithHistory);
        widgetMovePreviewRef.current?.setFile(null);

        handleMouseUp(
          currentProcessingFile,
          commitWithHistory,
          resizing,
          setResizing,
          setCurrCursor,
          moving,
          setMoving,
          isDrawing,
          latestDrawingRectRef.current,
          setIsDrawing,
          setRect,
          fixedInputs,
          isSelecting,
          setIsSelecting,
          setSelectionRect,
          setFreeSelectStartPoint,
        );
      },
    };
  });
  const pointerSchedulerRef = useRef(null);
  if (!pointerSchedulerRef.current) {
    pointerSchedulerRef.current = createCanvasPointerScheduler((point) => {
      const handlers = pointerHandlersRef.current;
      if (handlers?.active) handlers.move(point);
    });
  }

  useEffect(() => {
    const scheduler = pointerSchedulerRef.current;
    const finishInteraction = (event) => {
      const handlers = pointerHandlersRef.current;
      if (!handlers?.active) return;
      // Include a release inside the canvas, even if it precedes the next frame.
      if (
        event.type === "mouseup" &&
        pdfContainerRef.current?.contains(event.target)
      ) {
        scheduler.queue(event);
      }
      scheduler.flush();
      handlers.finish();
      handlers.active = false;
    };
    window.addEventListener("mouseup", finishInteraction);
    window.addEventListener("blur", finishInteraction);
    return () => {
      scheduler.cancel();
      widgetMoveControllerRef.current.cancel();
      window.removeEventListener("mouseup", finishInteraction);
      window.removeEventListener("blur", finishInteraction);
    };
  }, [pdfContainerRef]);

  // A queued event from the old page must not move fields on a new page.
  useLayoutEffect(() => {
    if (widgetMoveControllerRef.current.cancel()) {
      widgetMovePreviewRef.current?.setFile(null);
      setMoving(false);
      setResizing(false);
      setIsSelecting(false);
      setSelectionRect(null);
      setFreeSelectStartPoint(null);
      setCurrCursor("default");
    }

    return () => pointerSchedulerRef.current.cancel();
  }, [
    base64File,
    pdfInput,
    pageNumber,
    scale,
    displayRotation,
    setMoving,
    setResizing,
    setIsSelecting,
    setSelectionRect,
    setFreeSelectStartPoint,
    setCurrCursor,
  ]);

  useLayoutEffect(() => {
    pointerHandlersRef.current = {
      active: resizing || moving || isDrawing || isSelecting,
      move: (point) => {
        // Move/resize frames stay local; shared geometry is committed on release.
        const preview = widgetMovePreviewRef.current;
        const isBoxSelection =
          isSelecting && !moving && !resizing && !isDrawing;

        const useLocalMove = (moving || resizing || isBoxSelection) && preview;

        const controller = widgetMoveControllerRef.current;

        const gestureKey = isBoxSelection
          ? freeSelectStartPoint
          : initialMousePosition;

        const file = useLocalMove
          ? controller.read(
              currentProcessingFile,
              gestureKey,
              isBoxSelection ? "selection" : "geometry",
            )
          : currentProcessingFile;

        const updateFile = useLocalMove
          ? (updater) => preview.setFile(controller.update(updater))
          : setCurrentProcessingFile;
        handleMouseMove(
          point,
          file,
          updateFile,
          pdfContainerRef,
          resizing,
          initialMousePosition,
          initialInputSize,
          inputPosition,
          resizeDirection,
          moving,
          isDrawing,
          startPoint,
          setDrawingRectForFrame,
          isSelecting,
          freeSelectStartPoint,
          setSelectionRectForFrame,
          pageNumber,
        );
      },
      finish: () => {
        widgetMoveControllerRef.current.commit(setCurrentProcessingFile);
        widgetMovePreviewRef.current?.setFile(null);
        handleMouseUp(
          currentProcessingFile,
          setCurrentProcessingFile,
          resizing,
          setResizing,
          setCurrCursor,
          moving,
          setMoving,
          isDrawing,
          latestDrawingRectRef.current,
          setIsDrawing,
          setRect,
          fixedInputs,
          isSelecting,
          setIsSelecting,
          setSelectionRect,
          setFreeSelectStartPoint,
        );
      },
    };
  });

  if (!pointerSchedulerRef.current) {
    pointerSchedulerRef.current = createCanvasPointerScheduler((point) => {
      const handlers = pointerHandlersRef.current;
      if (handlers?.active) handlers.move(point);
    });
  }

  useEffect(() => {
    const scheduler = pointerSchedulerRef.current;
    const finishInteraction = (event) => {
      const handlers = pointerHandlersRef.current;
      if (!handlers?.active) return;
      // Include a release inside the canvas, even if it precedes the next frame.
      if (
        event.type === "mouseup" &&
        pdfContainerRef.current?.contains(event.target)
      ) {
        scheduler.queue(event);
      }
      scheduler.flush();
      handlers.finish();
      handlers.active = false;
    };
    window.addEventListener("mouseup", finishInteraction);
    window.addEventListener("blur", finishInteraction);
    return () => {
      scheduler.cancel();
      widgetMoveControllerRef.current.cancel();
      window.removeEventListener("mouseup", finishInteraction);
      window.removeEventListener("blur", finishInteraction);
    };
  }, [pdfContainerRef]);

  // A queued event from the old page must not move fields on a new page.
  useLayoutEffect(() => {
    if (widgetMoveControllerRef.current.cancel()) {
      widgetMovePreviewRef.current?.setFile(null);
      setMoving(false);
      setResizing(false);
      setIsSelecting(false);
      setSelectionRect(null);
      setFreeSelectStartPoint(null);
      setCurrCursor("default");
    }

    return () => pointerSchedulerRef.current.cancel();
  }, [
    base64File,
    pdfInput,
    pageNumber,
    scale,
    displayRotation,
    setMoving,
    setResizing,
    setIsSelecting,
    setSelectionRect,
    setFreeSelectStartPoint,
    setCurrCursor,
  ]);

  useLayoutEffect(() => {
    const arrows = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };

    const controller = createWidgetMovePreview();
    const heldKeys = new Set();

    let active = false;
    let insertedTextEditId = null;
    let frame = null;
    let pendingX = 0;
    let pendingY = 0;

    let commitTimer = null;

    const clearCommitTimer = () => {
      if (commitTimer !== null) {
        clearTimeout(commitTimer);
      }

      commitTimer = null;
    };

    const cancel = () => {
      clearCommitTimer();

      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      frame = null;
      pendingX = pendingY = 0;
      heldKeys.clear();
      controller.cancel();

      if (active) {
        widgetMovePreviewRef.current?.setFile(null);
      }

      active = false;
      insertedTextEditId = null;
    };

    const flush = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;

      if (!active || (!pendingX && !pendingY)) return;

      const dx = pendingX;
      const dy = pendingY;
      pendingX = pendingY = 0;

      const previewFile = controller.update((file) => {
        const temp = file.document.tempFields;

        if (insertedTextEditId) {
          const edit = temp.pdfTextEdits?.find(
            (candidate) =>
              candidate.edit_id === insertedTextEditId &&
              candidate.target_item_id === temp.selectedPdfTextItem?.id &&
              candidate.isEditorAdded === true &&
              candidate.kind === "inserted" &&
              candidate.action !== "delete" &&
              Number(candidate.page) === Number(pageNumber),
          );
          if (!edit?.rect || temp.editingInsertedTextId) return file;

          const currentDocument = temp.currentDocument || {};
          const x = Number(edit.rect.X);
          const y = Number(edit.rect.Y);
          const width = Number(edit.rect.W);
          const height = Number(edit.rect.H);
          const pageWidth = Number(currentDocument.actualWidth);
          const pageHeight = Number(currentDocument.actualHeight);
          const pageXMin = Number(currentDocument.pageXMin ?? 0);
          const pageYMin = Number(currentDocument.pageYMin ?? 0);
          if (
            ![
              x,
              y,
              width,
              height,
              pageWidth,
              pageHeight,
              pageXMin,
              pageYMin,
              dx,
              dy,
            ].every(Number.isFinite) ||
            width <= 0 ||
            height <= 0 ||
            pageWidth <= 0 ||
            pageHeight <= 0
          ) {
            return file;
          }

          const nextX = Math.max(
            pageXMin,
            Math.min(x + dx, pageXMin + pageWidth - width),
          );
          const nextY = Math.max(
            pageYMin,
            Math.min(y + dy, pageYMin + pageHeight - height),
          );
          if (nextX === x && nextY === y) return file;

          // Nudge only the insertion's position; keep its size and formatting.
          const rect = { ...edit.rect, X: nextX, Y: nextY };
          return {
            ...file,
            document: {
              ...file.document,
              tempFields: {
                ...temp,
                pdfTextEdits: temp.pdfTextEdits.map((candidate) =>
                  candidate === edit ? { ...edit, rect } : candidate,
                ),
                selectedPdfTextItem: { ...temp.selectedPdfTextItem, rect },
              },
            },
          };
        }

        const names = new Set(
          [
            temp.selectedFormField?.name,
            ...(temp.selectedExtraFields || []).map((field) => field.name),
          ].filter(Boolean),
        );

        const changed = new Map();

        const formFields = temp.formFields.map((field) => {
          if (!names.has(field.name)) return field;

          const moved = {
            ...field,
            x: field.x + dx,
            y: field.y + dy,
          };

          changed.set(field.name, moved);
          return moved;
        });

        return {
          ...file,
          document: {
            ...file.document,
            tempFields: {
              ...temp,
              formFields,
              selectedFormField:
                changed.get(temp.selectedFormField?.name) ||
                temp.selectedFormField,
              selectedExtraFields: (temp.selectedExtraFields || []).map(
                (field) => changed.get(field.name) || field,
              ),
            },
          },
        };
      });

      widgetMovePreviewRef.current?.setFile(previewFile);
    };

    const finish = () => {
      if (!active) return;

      // Apply the last pending movement before committing.
      flush();

      controller.commit((update) => {
        const next = update(currentProcessingFile);
        if (next === currentProcessingFile) return;

        undoRedoStats?.pushHistory?.();
        setCurrentProcessingFile(update);
      });

      cancel();
    };

    const finishBeforeOtherAction = () => {
      if (!active) return;

      clearCommitTimer();
      flushSync(() => finish());
    };

    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;

      const direction = arrows[e.key];

      if (!direction) {
        // Modifier keys alone should not end the movement.
        if (!["Shift", "Control", "Alt", "Meta"].includes(e.key)) {
          finishBeforeOtherAction();
        }

        // Let the existing handler process Undo, Delete, Save, etc.
        return;
      }

      const target = e.target;
      const isEditingText =
        (target?.tagName === "INPUT" && !target.readOnly) ||
        ["TEXTAREA", "SELECT"].includes(target?.tagName) ||
        target?.isContentEditable;

      const temp = currentProcessingFile?.document?.tempFields;
      const hasSelectedWidgets = Boolean(
        temp?.selectedFormField || temp?.selectedExtraFields?.length,
      );
      const selectedInsertedText =
        !hasSelectedWidgets &&
        activeEditorTool === "edit" &&
        selectToolActive &&
        !temp?.editingInsertedTextId
          ? temp?.pdfTextEdits?.find(
              (edit) =>
                edit.edit_id &&
                edit.target_item_id === temp.selectedPdfTextItem?.id &&
                edit.isEditorAdded === true &&
                edit.kind === "inserted" &&
                edit.action !== "delete" &&
                edit.rect &&
                Number(edit.page) === Number(pageNumber),
            )
          : null;

      if (
        isEditingText ||
        (selectedInsertedText && isEditableTarget(target)) ||
        (!hasSelectedWidgets && !selectedInsertedText) ||
        !temp?.currentDocument?.base64File ||
        !widgetMovePreviewRef.current
      ) {
        return;
      }

      // Prevent the old keyboard helper from moving the same widgets again.
      e.preventDefault();
      e.stopPropagation();

      if (moving || resizing || isDrawing || isSelecting) return;

      const step = e.ctrlKey ? 1 : 5;

      const delta = canvasDeltaToPdfDelta(
        direction[0] * step,
        direction[1] * step,
        temp.currentDocument.viewport,
        temp.currentDocument.scaleFactor,
        true,
      );

      if (
        !Number.isFinite(delta.x) ||
        !Number.isFinite(delta.y) ||
        (!delta.x && !delta.y)
      ) {
        return;
      }

      clearCommitTimer();
      const isFirstStep = !active;

      if (isFirstStep) {
        insertedTextEditId = selectedInsertedText?.edit_id || null;
        controller.read(
          currentProcessingFile,
          insertedTextEditId ? { insertedTextEditId } : {},
        );
        active = true;
      }

      heldKeys.add(e.key);

      pendingX += delta.x;
      pendingY += delta.y;

      if (isFirstStep || !e.repeat) {
        // Every individual tap updates the preview immediately.
        flush();
      } else if (frame === null) {
        // Held-key repeats remain batched.
        frame = requestAnimationFrame(flush);
      }
    };

    const onKeyUp = (e) => {
      if (!heldKeys.delete(e.key)) return;

      if (!heldKeys.size) {
        flush();
        clearCommitTimer();

        commitTimer = setTimeout(() => {
          commitTimer = null;
          finish();
        }, 180);
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("pointerdown", finishBeforeOtherAction, true);
    window.addEventListener("wheel", finishBeforeOtherAction, {
      capture: true,
      passive: true,
    });
    window.addEventListener("blur", finishBeforeOtherAction);

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("pointerdown", finishBeforeOtherAction, true);
      window.removeEventListener("wheel", finishBeforeOtherAction, true);
      window.removeEventListener("blur", finishBeforeOtherAction);
      cancel();
    };
  }, [
    currentProcessingFile,
    activeEditorTool,
    selectToolActive,
    pageNumber,
    scale,
    displayRotation,
    base64File,
    pdfInput,
    moving,
    resizing,
    isDrawing,
    isSelecting,
    undoRedoStats?.pushHistory,
    setCurrentProcessingFile,
  ]);

  if (!(base64File || pdfInput)) return null;

  return (
    <Box
      ref={pdfScrollContainerRef}
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        minHeight: 0,
        alignSelf: "stretch",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "flex-start",
        overflow: "auto",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          margin: "auto",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(228, 228, 228, 0.5)",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Box
          ref={pdfContainerRef}
          style={{
            width: displayViewport?.width ?? "auto",
            height: displayViewport?.height ?? "auto",
          }}
          sx={{
            position: "relative",
            display: "inline-block",
            border: "2px solid #d3d3d3",
            borderRadius: "18px",
            boxShadow: "3px 4px 12px rgba(0, 0, 0, 0.3)",
            backgroundColor: "#fff",
            cursor: currCursor || "default",
            userSelect: "none",
          }}
          onMouseDown={(e) => {
            const isToolActive = Object.values(
              currentProcessingFile?.document?.tempFields?.formFieldTools || {},
            ).some((field) => field.active);

            if (isToolActive) {
              handleMouseDown(
                e,
                currentProcessingFile,
                setCurrentProcessingFile,
                null,
                null,
                "fieldTools",
                "crosshair",
                pdfContainerRef,
                setStartPoint,
                setRect,
                setIsDrawing,
                setCurrCursor,
                setFreeSelectStartPoint,
                setMoving,
                setIsSelecting,
                setInitialMousePosition,
                setInitialInputSize,
                setInputPosition,
                setSelectionRect,
                setResizing,
                setResizeDirection,
                pageNumber,
              );
            } else if (selectToolActive) {
              handleMouseDown(
                e,
                currentProcessingFile,
                setCurrentProcessingFile,
                null,
                null,
                "freeSelect",
                "default",
                pdfContainerRef,
                setStartPoint,
                setRect,
                setIsDrawing,
                setCurrCursor,
                setFreeSelectStartPoint,
                setMoving,
                setIsSelecting,
                setInitialMousePosition,
                setInitialInputSize,
                setInputPosition,
                setSelectionRect,
                setResizing,
                setResizeDirection,
                pageNumber,
              );
            }
          }}
          onMouseMove={(e) => {
            if (resizing || moving || isDrawing || isSelecting) {
              pointerSchedulerRef.current.queue(e);
            }
          }}
        >
          {/* Visual zoom is immediate; PDF layers redraw only after zoom settles.
              Field interactions do not restart the PDF rendering tasks. */}
          <StablePdfPage
            file={base64File || pdfInput}
            pageNumber={pageNumber}
            scale={scale}
            displayRotation={displayRotation}
            pdfDocumentRef={pdfDocumentRef}
            onDocumentLoadSuccessRef={onDocumentLoadSuccessRef}
            onPageLoadSuccessRef={onPageLoadSuccessRef}
            buildPdfTextItemsForPageRef={buildPdfTextItemsForPageRef}
          />

          <InteractionRectOverlay
            ref={interactionRectRef}
            isDrawing={isDrawing}
            isSelecting={isSelecting}
            rect={rect}
            selectionRect={selectionRect}
            scaleFactor={scaleFactor}
            activeEditorTool={activeEditorTool}
          />

          <FieldToolPreviewOverlay
            fields={
              currentProcessingFile?.document?.tempFields?.formFieldTools
                ?.textField?.values || []
            }
          />

          <PdfTextOverlayLayer
            currentProcessingFile={currentProcessingFile}
            pageNumber={pageNumber}
            selectToolActive={selectToolActive}
            highlightFields={highlightFields}
            setCurrentProcessingFile={setCurrentProcessingFile}
            textSelectionMode={textSelectionMode}
          />

          <FormFieldOverlayLayer
            widgetMovePreviewRef={widgetMovePreviewRef}
            currentProcessingFile={currentProcessingFile}
            pageNumber={pageNumber}
            scale={scale}
            selectToolActive={selectToolActive}
            typingToolActive={typingToolActive}
            highlightFields={highlightFields}
            textOverlayDisplayMode={textOverlayDisplayMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            currCursor={currCursor}
            unassignedFieldNames={unassignedFieldNames}
            fixedInputs={fixedInputs}
            inputRef={inputRef}
            childInputRef={childInputRef}
            pdfContainerRef={pdfContainerRef}
            setCurrentProcessingFile={setCurrentProcessingFile}
            setStartPoint={setStartPoint}
            setRect={setRect}
            setIsDrawing={setIsDrawing}
            setCurrCursor={setCurrCursor}
            setFreeSelectStartPoint={setFreeSelectStartPoint}
            setMoving={setMoving}
            setIsSelecting={setIsSelecting}
            setInitialMousePosition={setInitialMousePosition}
            setInitialInputSize={setInitialInputSize}
            setInputPosition={setInputPosition}
            setSelectionRect={setSelectionRect}
            setResizing={setResizing}
            setResizeDirection={setResizeDirection}
            handleMouseDown={handleMouseDown}
            handleFieldChange={handleFieldChange}
            undoRedoStats={undoRedoStats}
            activeEditorTool={activeEditorTool}
            formFields={formFields}
            onNavigateToTaggedField={onNavigateToTaggedField}
            onFavouriteDrop={onFavouriteDrop}
            favouriteDropDisabled={favouriteDropDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PDFCanvas;
